/**
 * PDF export presets — audience-driven section allowlists for the Discovery report.
 *
 * Phase 1 approach:
 *   - Hard-coded presets (no per-section customize tray yet).
 *   - Two sections are ALWAYS included regardless of preset: `cover` and
 *     `recommended_next_step`. This prevents nonsensical PDFs (e.g. no closing
 *     recommendation, or no client name).
 *   - Sections `executive_snapshot` and `commercial_impact` are also always
 *     included because every audience needs the headline decision + business
 *     framing.
 *
 * The section keys below MUST match the guard strings used in pdf-generator.js.
 */

export const SECTION_KEYS = {
  COVER: 'cover',                              // always on
  AUDITOR_METADATA: 'auditor_metadata',        // Auditor/Assessor only (page 2)
  EXECUTIVE_SNAPSHOT: 'executive_snapshot',    // always on
  COMMERCIAL_IMPACT: 'commercial_impact',      // always on
  HOW_TO_READ: 'how_to_read',
  GATING_ANALYSIS: 'gating_analysis',
  EVIDENCE_READINESS: 'evidence_readiness',
  DISP_GAP_ANALYSIS: 'disp_gap_analysis',
  TECHNICAL_SUMMARY: 'technical_summary',              // Auditor/Assessor only
  CSQ_SUBMISSION_MAPPING: 'csq_submission_mapping',    // Auditor/Assessor only
  TECHNOLOGY_STACK_STATUS: 'technology_stack_status',
  PATH_TO_DISP_READINESS: 'path_to_disp_readiness',
  RECOMMENDED_NEXT_STEP: 'recommended_next_step', // always on
  ASSESSOR_SIGNATURE: 'assessor_signature',            // Auditor/Assessor only (final page)
};

// Always-on sections (invariant across every preset)
const CORE = [
  SECTION_KEYS.COVER,
  SECTION_KEYS.EXECUTIVE_SNAPSHOT,
  SECTION_KEYS.COMMERCIAL_IMPACT,
  SECTION_KEYS.RECOMMENDED_NEXT_STEP,
];

// Preset definitions. Estimated page ranges are calibrated against SecureTech Inc's
// full report (iteration_41 = 16 pages) and will vary slightly with assessment size.
export const PDF_PRESETS = {
  board_pack: {
    id: 'board_pack',
    label: 'Board Pack',
    description: 'Cover, Executive Snapshot, Commercial Impact and Next Step. For board-level readers.',
    estimated_pages: '3-5',
    sections: [...CORE],
  },
  executive_pack: {
    id: 'executive_pack',
    label: 'Executive Pack',
    description: 'Adds Gating Control Assessment, Evidence Readiness and Path to Readiness. For CIO / CISO / executive owners.',
    estimated_pages: '8-11',
    sections: [
      ...CORE,
      SECTION_KEYS.GATING_ANALYSIS,
      SECTION_KEYS.EVIDENCE_READINESS,
      SECTION_KEYS.PATH_TO_DISP_READINESS,
    ],
  },
  full_discovery: {
    id: 'full_discovery',
    label: 'Full Discovery',
    description: 'Complete Discovery report including DISP Gap Analysis and Technology Stack Status. For internal delivery and Accelerator handoff.',
    estimated_pages: '15-19',
    sections: [
      ...CORE,
      SECTION_KEYS.HOW_TO_READ,
      SECTION_KEYS.GATING_ANALYSIS,
      SECTION_KEYS.EVIDENCE_READINESS,
      SECTION_KEYS.DISP_GAP_ANALYSIS,
      SECTION_KEYS.TECHNOLOGY_STACK_STATUS,
      SECTION_KEYS.PATH_TO_DISP_READINESS,
    ],
  },
  auditor_assessor: {
    id: 'auditor_assessor',
    label: 'Auditor / Assessor',
    description: 'Full Discovery plus an Auditor Metadata context page, Technical Summary (CES / PGS / CMS / Audit Risk), CSQ Submission Mapping (per-category ready-to-submit responses, evidence, minimum viable path) and a formal Assessor Attestation page. For Defence submission, external audit or internal governance review.',
    estimated_pages: '24-28',
    sections: [
      ...CORE,
      SECTION_KEYS.AUDITOR_METADATA,
      SECTION_KEYS.HOW_TO_READ,
      SECTION_KEYS.GATING_ANALYSIS,
      SECTION_KEYS.EVIDENCE_READINESS,
      SECTION_KEYS.DISP_GAP_ANALYSIS,
      SECTION_KEYS.TECHNICAL_SUMMARY,
      SECTION_KEYS.CSQ_SUBMISSION_MAPPING,
      SECTION_KEYS.TECHNOLOGY_STACK_STATUS,
      SECTION_KEYS.PATH_TO_DISP_READINESS,
      SECTION_KEYS.ASSESSOR_SIGNATURE,
    ],
  },
};

export const DEFAULT_PRESET_ID = 'executive_pack';
export const PDF_PRESET_ORDER = ['board_pack', 'executive_pack', 'full_discovery', 'auditor_assessor'];

/**
 * Resolve a preset id (or a raw section array) into a Set of enabled section
 * keys. Always includes the CORE always-on sections.
 */
export const resolveEnabledSections = (presetOrSections) => {
  // Accept an explicit section array (future customize tray) or a preset id.
  // Always merge CORE so a preset definition that forgets to include an
  // always-on section (or a caller that passes only a partial array) still
  // produces a valid PDF.
  if (Array.isArray(presetOrSections)) {
    return new Set([...CORE, ...presetOrSections]);
  }
  const preset = PDF_PRESETS[presetOrSections] || PDF_PRESETS[DEFAULT_PRESET_ID];
  return new Set([...CORE, ...preset.sections]);
};
